<template>
  <div class="careers-page">
    <div class="careers-intro-wrap">
      <LazyBg :src="introBg" fit="fill" />
      <p class="careers-intro" data-aos="fade-up">
        以奋斗者为本，以价值创造者为荣。海曦技术坚持技术向善、自主创新、开放共赢的人才文化，为每一位成员提供清晰的成长路径、有竞争力的薪酬激励和广阔的事业平台，与公司共同成长、共享成果。
      </p>
    </div>
    <section class="careers-inner container">


      <div v-if="jobs.length" class="careers-split" data-aos="fade-up" data-aos-delay="80">
        <aside class="careers-nav" aria-label="在招职位">
          <ul class="careers-nav-list" role="tablist">
            <li v-for="(job, i) in jobs" :key="job.id" role="presentation">
              <button type="button" role="tab" class="careers-nav-item" :class="{ 'is-active': i === activeIndex }"
                :aria-selected="i === activeIndex" :aria-controls="`panel-${job.id}`" :id="`tab-${job.id}`"
                @click="selectJob(i)">
                <span class="careers-nav-label">{{ job.title }}</span>
                <span class="careers-nav-chevron" aria-hidden="true">›</span>
              </button>
            </li>
          </ul>
        </aside>

        <div class="careers-detail-wrap">
          <Transition name="career-panel" mode="out-in">
            <article v-if="activeJob" :id="`panel-${activeJob.id}`" :key="activeJob.id" class="careers-detail"
              role="tabpanel" :aria-labelledby="`tab-${activeJob.id}`">
              <h2 class="careers-detail-title">{{ activeJob.title }}</h2>
              <p v-if="detailLoading" class="careers-detail-loading">加载职位详情…</p>
              <ul v-else class="careers-detail-list">
                <li v-for="(line, j) in activeJob.bullets" :key="j" :class="{ 'is-heading': isSectionHeading(line) }">
                  {{ line }}
                </li>
              </ul>
              <p v-if="activeJob.email" class="careers-detail-email">
                <button type="button" class="careers-apply-btn" @click="sendEmail(activeJob.email)">
                  一键投递
                </button>
              </p>
            </article>
          </Transition>
        </div>
      </div>

      <p v-else-if="!listLoading" class="careers-empty">暂无在招职位</p>
    </section>
  </div>
</template>

<script setup lang="ts">
import LazyBg from '@/components/LazyBg.vue';
import introBg from '@/assets/workBg.png';
import { computed, ref, watch } from 'vue';
import { useSeo } from '@/composables/useSeo';
import { useFrontCms } from '@/composables/useFrontCms';
import { useFrontCmsStore } from '@/stores/frontCms';
import { mapJobDetailToPost, mapJobsToPosts, type JobPostView } from '@/utils/frontCmsMappers';

const cms = useFrontCmsStore();
const { recruitments, syncing } = useFrontCms('recruitments');

const FALLBACK_JOBS: JobPostView[] = [
  {
    id: 'algo',
    title: '算法工程师',
    bullets: [
      '岗位职责',
      '负责多模态算法研发、模型训练与优化',
      '参与心理筛查、医疗影像、视觉识别等场景算法落地',
      '任职要求',
      '要求：计算机/AI相关专业，熟悉PyTorch/TensorFlow，有大模型、CV、NLP经验优先'
    ]
  },
  {
    id: 'backend',
    title: '后端开发工程师',
    bullets: [
      '岗位职责',
      '参与核心业务服务设计与开发，保障高可用与可扩展架构',
      '负责接口、数据层与中间件集成，配合算法与前端完成端到端交付',
      '任职要求',
      '要求：熟练掌握 Java/Go 之一，熟悉分布式、消息队列与云原生部署经验优先'
    ]
  },
  {
    id: 'pm',
    title: '产品经理',
    bullets: [
      '岗位职责',
      '梳理政企与教育等行业场景需求，输出产品方案与路线图',
      '推动跨团队评审与迭代节奏，跟进上线效果与数据反馈',
      '任职要求',
      '要求：3 年以上 B 端产品经验，有 AI/大数据类产品背景者优先'
    ]
  }
];

const listLoading = computed(() => !!syncing.value.recruitments);

const jobs = computed(() => {
  if (!recruitments.value.length) {
    return FALLBACK_JOBS;
  }
  return recruitments.value.map((item) => {
    const id = String(item.id ?? item.title);
    const detail = cms.getRecruitmentDetail(id);
    if (detail) {
      return mapJobDetailToPost(detail);
    }
    return mapJobsToPosts([item])[0];
  });
});

const activeIndex = ref(0);

const activeJob = computed(() => jobs.value[activeIndex.value] ?? null);

const detailLoading = computed(() => {
  const job = activeJob.value;
  if (!job || !recruitments.value.length) {
    return false;
  }
  return !cms.getRecruitmentDetail(job.id) && !!syncing.value.recruitmentDetail;
});

watch(
  () => recruitments.value.map((item) => String(item.id ?? '')),
  (ids) => {
    if (!ids.length) {
      return;
    }
    cms.hydrateFromCache();
    void cms.syncRecruitmentDetails(ids);
  },
  { immediate: true }
);

watch(jobs, (list) => {
  if (activeIndex.value >= list.length) {
    activeIndex.value = 0;
  }
});

const selectJob = (index: number) => {
  activeIndex.value = index;
  const job = jobs.value[index];
  if (job?.id) {
    void cms.syncRecruitmentDetail(job.id);
  }
};

function isSectionHeading(line: string): boolean {
  return line === '岗位职责' || line === '任职要求';
}

function sendEmail(email: string) {
  window.open(email, '_blank');
}

useSeo({
  title: '人才招聘',
  description: '海曦人才招聘页面，了解岗位机会、团队文化与发展路径。'
});
</script>

<style scoped lang="scss">
.careers-page {
  min-height: calc(100vh - 120px);
  padding: 65px 0 80px;
  background: #ffffff;
  color: #333333;
}

.careers-intro-wrap {
  position: relative;
  padding: 64px 0 68px;
  overflow: hidden;

  p {
    position: relative;
    z-index: 1;
    width: max(1120px, calc(100% - 800px));
    margin: 0 auto;
  }
}

.careers-inner {
  margin: 0 auto;
}

.careers-intro {
  margin: 0 0 40px;

  /* Noto Sans SC Medium 16 28 */
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;

}

.careers-empty {
  margin: 0;
  padding-left: 315px;
  color: #666;
  font-size: 14px;
}

.careers-split {
  display: grid;
  grid-template-columns: minmax(200px, 280px) 1fr;
  gap: 0;
  align-items: start;
  padding-top: 8px;
}

.careers-detail {
  border-radius: 8px;
  border: 1px solid #EFEFEF;
  background: #FFF;
  padding: 16px;
}

.careers-nav {
  border-right: 1px solid #e5e5e5;
  padding-right: 8px;
}

.careers-nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.careers-nav-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 16px 20px 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  font-size: 16px;
  line-height: 1.45;
  color: #5c5c5c;
  transition:
    color 0.2s ease,
    background-color 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%) scaleY(0);
    width: 2px;
    height: 0;
    border-radius: 999px;
    background: #0095FF;
    transition:
      transform 0.25s ease,
      height 0.25s ease;
  }

  &:hover {
    color: #3a3a3a;
    background: rgba(72, 201, 176, 0.06);
  }

  &.is-active {
    color: #0095FF;
    background: rgba(72, 201, 176, 0.08);

    &::before {
      height: 100%;
      transform: translateY(-50%) scaleY(1);
    }

    .careers-nav-chevron {
      color: #0095FF;
    }
  }
}

.careers-nav-chevron {
  flex-shrink: 0;
  font-size: 22px;
  line-height: 1;
  font-weight: 300;
  color: #9a9a9a;
}

.careers-detail-wrap {
  padding: 8px 0 24px 32px;
  min-height: 280px;
}

.careers-detail-title {
  margin: 0 0 10px;
  font-size: 18px;
  font-weight: 500;
  line-height: 32px;
  background: rgba(0, 149, 255, 0.10);
  padding: 12px 16px;
  text-align: center;
}

.careers-detail-loading {
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
}

.careers-detail-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.careers-detail-list li {
  position: relative;
  padding-left: 1em;
  font-size: 14px;
  line-height: 24px;

  &::before {
    content: '·';
    position: absolute;
    left: 0;
    font-weight: 700;
    color: #666666;
  }

  &.is-heading {
    padding-left: 0;
    margin-top: 8px;
    font-weight: 700;

    &::before {
      content: none;
    }

    &:first-child {
      margin-top: 0;
    }
  }
}

.careers-detail-email {
  margin: 20px 0 0;
  font-size: 14px;
  line-height: 24px;

  a {
    color: #0095FF;
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

.careers-apply-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 6px;
  background: #0095ff;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #0080db;
  }
}

.career-panel-enter-active,
.career-panel-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.28s ease;
}

.career-panel-enter-from {
  opacity: 0;
  transform: translateX(14px);
}

.career-panel-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

@media (max-width: 820px) {

  .careers-intro,
  .careers-empty {
    padding-left: 0;
  }

  .careers-split {
    grid-template-columns: 1fr;
  }

  .careers-nav {
    border-right: none;
    border-bottom: 1px solid #e5e5e5;
    padding-right: 0;
    padding-bottom: 8px;
    margin-bottom: 8px;
  }

  .careers-detail-wrap {
    padding: 16px 0 32px;
    min-height: 220px;
  }
}
</style>
